import React, { useEffect, useRef, useState } from "react";
import { Group, Checkbox, Modal, FileInput, Text, TextInput, Divider, Space } from "@mantine/core";
import AceEditor from "react-ace";
import ace from "ace-builds";
import useWebSocket from 'react-use-websocket';
import Select from 'react-select';
import { FaChevronDown } from "react-icons/fa";
import { FaSpinner } from 'react-icons/fa';
import { FaTerminal } from "react-icons/fa";
import { FaHistory } from "react-icons/fa";
import { useQuery, useMutation } from '@apollo/client';
import { GET_USER_SCRIPTS, SAVE_USER_SCRIPT, DELETE_USER_SCRIPT } from '../../../queries/user-script';
import { GET_LAST_EXECUTIONS } from '../../../queries/code-execution';
import { ADD_LOG } from "../../../queries/user-analytics";
import { CODE_EXECUTOR_WS_URL } from '../../../config/endpoints';
import { AUTH_TOKEN } from '../../../constants/authToken';

import "ace-builds/src-noconflict/theme-monokai";
import "ace-builds/src-noconflict/mode-c_cpp";
import "ace-builds/src-noconflict/mode-php";
import "ace-builds/src-noconflict/mode-python";
import "ace-builds/src-noconflict/mode-javascript";

import "./CodeEditor.css";

const LS_DRAFT    = 'codeEditor_draft';
const LS_LANG     = 'codeEditor_language';
const LS_OUTPUT   = 'codeEditor_output';
const LS_LAST_RUN = 'codeEditor_lastRun';

function loadLastRun() {
  try { return JSON.parse(localStorage.getItem(LS_LAST_RUN)) || null; }
  catch { return null; }
}

export default function OnlineIDE({ user }) {
  const [editor, setEditor] = useState(null);
  const [codeText, setCodeText] = useState(() => localStorage.getItem(LS_DRAFT) || "");
  const [output, setOutput] = useState(() => localStorage.getItem(LS_OUTPUT) || "");
  const [selectedLanguage, setSelectedLanguage] = useState(() => {
    try { return JSON.parse(localStorage.getItem(LS_LANG)) || ""; }
    catch { return ""; }
  });
  const [isLoading, setIsLoading] = useState(false);
  const [wsReady, setWsReady] = useState(false); // true once server confirms auth
  const [isSavePopupVisible, setIsSavePopupVisible] = useState(false);
  const [scriptNameInput, setScriptNameInput] = useState("");
  const [scriptsPopupVisible, setScriptsPopupVisible] = useState(false);
  const [outputActive, setOutputActive] = useState(true);
  const [lastRunVisible, setLastRunVisible] = useState(false);
  const [lastRun, setLastRun] = useState(loadLastRun);

  const draftSaveTimer = useRef(null);

  const token = localStorage.getItem(AUTH_TOKEN);
  const WS_URL = token ? CODE_EXECUTOR_WS_URL : null;

  const { sendMessage, lastMessage } = useWebSocket(WS_URL, {
    onOpen: () => {
      console.log('WebSocket connection established.');
      sendMessage(JSON.stringify({
        type: 'auth',
        userid: user.userid,
        token,
      }));
    },
    onClose: () => {
      console.log('WebSocket connection closed.');
      setWsReady(false);
    },
    shouldReconnect: () => true,
  });

  const [logEventMutation] = useMutation(ADD_LOG, {
    onCompleted: () => {
        const { status, message } = logEvent;
        if (status === "success") {
            console.log("Event logged successfully:", message);
        } else {
            console.error("Failed to log event:", message);
        }
    },
    onError: (error) => {
        console.error("Error logging event:", error.message);
    }
  });

  const logEvent = (eventType, additionalData, _userid) => {
    const eventData = {
        userid: Number(_userid),
        event_type: eventType,
        timestamp: new Date().toISOString(),
        additional_data: JSON.stringify(additionalData)
    };
    logEventMutation({ variables: eventData });
  };

  const { data: lastExecutionsData, refetch: refetchLastExecutions } = useQuery(GET_LAST_EXECUTIONS, {
    variables: { userID: user.userid, limit: 5 },
  });

  useEffect(() => {
    const aceEditor = ace.edit("editor");
    aceEditor.setTheme("ace/theme/monokai");
    aceEditor.session.setMode("ace/mode/c_cpp");
    setEditor(aceEditor);
    return () => { aceEditor.destroy(); };
  }, []);

  useEffect(() => {
    if (!lastMessage) return;
    const messageData = JSON.parse(lastMessage.data);

    if (messageData.type === 'output') {
      setOutput(prev => {
        const next = (prev || '') + messageData.data + '\n';
        localStorage.setItem(LS_OUTPUT, next);
        return next;
      });
    } else if (messageData.type === 'buffered_output') {
      // Output that arrived while the tab was closed — prepend with marker
      setOutput(prev => {
        const separator = '── output while disconnected ──\n';
        const next = separator + messageData.data + '\n── reconnected ──\n' + (prev || '');
        localStorage.setItem(LS_OUTPUT, next);
        return next;
      });
    } else if (messageData.type === 'loading') {
      setIsLoading(true);
      setOutput("");
      localStorage.setItem(LS_OUTPUT, "");
    } else if (messageData.type === 'executionFinished') {
      setIsLoading(false);
      const snapshot = {
        output: localStorage.getItem(LS_OUTPUT) || '',
        timestamp: new Date().toISOString(),
        language: selectedLanguage?.label || 'Python',
      };
      localStorage.setItem(LS_LAST_RUN, JSON.stringify(snapshot));
      setLastRun(snapshot);
      refetchLastExecutions();
    } else if (messageData.type === 'status') {
      setIsLoading(messageData.executing);
      setWsReady(true);
    } else if (messageData.type === 'error') {
      setIsLoading(false);
      setOutput(prev => (prev || '') + '\nError: ' + messageData.error + '\n');
    }
  }, [lastMessage]);

  const languageOptions = [
    { value: "python", label: "Python" },
  ];

  const handleLanguageChange = (selectedOption) => {
    setSelectedLanguage(selectedOption);
    localStorage.setItem(LS_LANG, JSON.stringify(selectedOption));
    changeLanguage(selectedOption.value);
  };

  const changeLanguage = (language) => {
    if (language === "c" || language === "cpp")
      editor.session.setMode("ace/mode/c_cpp");
    else if (language === "php") editor.session.setMode("ace/mode/php");
    else if (language === "python") editor.session.setMode("ace/mode/python");
    else if (language === "node") editor.session.setMode("ace/mode/javascript");
  };

  const executeCode = () => {
    if (isLoading) return;
    setIsLoading(true);
    setOutput("");
    localStorage.setItem(LS_OUTPUT, "");

    const requestData = { language: selectedLanguage.value, code: codeText };
    const message = { type: 'execute', data: requestData };
    logEvent("Code Editor - Execute", message, user.userid);
    sendMessage(JSON.stringify(message));
  };

  const onChangeHandler = (code) => {
    setCodeText(code);
    if (draftSaveTimer.current) clearTimeout(draftSaveTimer.current);
    draftSaveTimer.current = setTimeout(() => {
      localStorage.setItem(LS_DRAFT, code);
    }, 500);
  };

  const toggleOutput = () => {
    setOutputActive(!outputActive);
    setStartTime(formatStartTime(Date.now()));
  };

  const formatStartTime = (time) => new Intl.DateTimeFormat(navigator.language, {
    dateStyle: "full",
    timeStyle: "long",
  }).format(time);

  const [startTime, setStartTime] = useState(formatStartTime(Date.now()));

  const stopExecutionHandler = () => {
    sendMessage(JSON.stringify({ type: "executionFinished" }));
  };

  const [isScriptOptionsVisible, setIsScriptOptionsVisible] = useState(false);

  const handleScriptOptionClick = (option) => {
    if (option === "load") handleScriptLoad();
    else if (option === "save") toggleSavePopup();
    else if (option === "import") toggleImportPopup();
    setIsScriptOptionsVisible(false);
  };

  const toggleScriptOptions = () => setIsScriptOptionsVisible((prev) => !prev);

  const { data: scriptsData, refetch } = useQuery(GET_USER_SCRIPTS, { variables: { userID: user.userid } });
  const [saveUserScript] = useMutation(SAVE_USER_SCRIPT);
  const [deleteUserScript] = useMutation(DELETE_USER_SCRIPT);

  const toggleScriptsPopup = () => setScriptsPopupVisible((prev) => !prev);

  const handleLoadScriptClick = (script) => {
    setCodeText(script.script_content);
    localStorage.setItem(LS_DRAFT, script.script_content);
    setOutput("Successfully loaded script: " + JSON.stringify(script));
    toggleScriptsPopup();
  };

  const handleScriptLoad = async () => {
    try {
      toggleScriptsPopup();
    } catch (error) {
      console.error('Error loading script:', error.message);
      setOutput("Error loading script: " + error.message);
    }
  };

  const toggleSavePopup = () => setIsSavePopupVisible((prev) => !prev);

  const handleSavePopupBack = () => {
    setScriptNameInput("");
    setIsSavePopupVisible(false);
  };

  const handleSavePopupSave = async () => {
    if (!user) return;
    if (!codeText.trim()) {
      setOutput("Script content is empty. Not saving.");
      setIsSavePopupVisible(false);
      return;
    }
    const variables = { userID: user.userid, script_name: scriptNameInput, script_content: codeText };
    try {
      const result = await saveUserScript({ variables });
      setOutput("Script saved successfully:" + JSON.stringify(result.data.saveUserScript));
      setScriptNameInput("");
      setIsSavePopupVisible(false);
      refetch();
    } catch (error) {
      console.error("Error saving user script:", error.message);
    }
  };

  const handleDeleteScriptClick = async (script) => {
    try {
      const result = await deleteUserScript({
        variables: { script_id: script.script_id, script_name: script.script_name },
      });
      setOutput("Script deleted successfully:" + JSON.stringify(result.data.deleteUserScript));
      refetch();
      toggleScriptsPopup();
    } catch (error) {
      console.error("Error deleting user script:", error.message);
      throw error;
    }
  };

  const [selectedScripts, setSelectedScripts] = useState([]);

  const toggleSelectScript = (script) => {
    const index = selectedScripts.findIndex((s) => s.script_id === script.script_id);
    if (index === -1) {
      setSelectedScripts([...selectedScripts, script]);
    } else {
      const updated = [...selectedScripts];
      updated.splice(index, 1);
      setSelectedScripts(updated);
    }
  };

  const toggleAllScripts = () => {
    if (selectedScripts.length === scriptsData.getUserScripts.length) {
      setSelectedScripts([]);
    } else {
      setSelectedScripts([...scriptsData.getUserScripts]);
    }
  };

  const handleDeleteSelectedScripts = async () => {
    try {
      await Promise.all(selectedScripts.map((script) =>
        deleteUserScript({ variables: { script_id: script.script_id, script_name: script.script_name } })
      ));
      setOutput(`Deleted ${selectedScripts.length} scripts successfully.`);
      refetch();
      setSelectedScripts([]);
      toggleScriptsPopup();
    } catch (error) {
      console.error("Error deleting scripts:", error.message);
      throw error;
    }
  };

  const [isImportPopupVisible, setIsImportPopupVisible] = useState(false);

  const handleImportFile = async (file) => {
    if (!file) return;
    const fileType = file.name.split('.').pop().toLowerCase();
    if (fileType !== 'py') {
      setOutput("Invalid file type. Only Python (.py) files are allowed.");
      setIsImportPopupVisible(false);
      return;
    }
    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target.result;
      setCodeText(content);
      localStorage.setItem(LS_DRAFT, content);
      setIsImportPopupVisible(false);
    };
    reader.readAsText(file);
  };

  const toggleImportPopup = () => setIsImportPopupVisible((prev) => !prev);

  const formatRunTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    return new Intl.DateTimeFormat(navigator.language, { dateStyle: 'medium', timeStyle: 'short' }).format(d);
  };

  const lastDbRuns = lastExecutionsData?.getLastExecutions || [];

  return (
    <div>
      <div className="callout callout-danger">
        <h4>Code Editor</h4>
        <span>
          <p>
            1. Users need to manually refresh the page to see orders placed/cancelled/modified by the trading algo.
          </p>
          <p>
            2. Users may occasionally need to click the 'stop' button multiple times to halt the algorithm.
          </p>
          <p>
            3. Your script and its output are saved automatically. If you refresh or close the tab, the algorithm
            keeps running on the server and you can reconnect to see the output.
          </p>
          <p>
            4. <strong>Pre-defined variables:</strong> <code>connector</code> (a ready-to-use{' '}
            <code>NUSwapConnector</code> instance), <code>USERID</code>, and <code>WALLETID</code> are
            automatically available in every script — no need to call{' '}
            <code>NUSwapConnector(token)</code> yourself. Example:{' '}
            <code>balance = connector.getUserBalance()</code>
          </p>
        </span>
      </div>
      <div className="control-panel">
        <div className="left-panel">
          <span className="name">Code Editor</span>
        </div>
        <div className="script-dropdown-container">
          <div className="script-button" onClick={toggleScriptOptions}>
            Script <FaChevronDown className="script-arrow" />
          </div>
          {isScriptOptionsVisible && (
            <div className="script-dropdown">
              <div className="script-btn" onClick={() => handleScriptOptionClick("import")}>Import</div>
              <div className="script-btn" onClick={() => handleScriptOptionClick("load")}>Load</div>
              <div className="script-btn" onClick={() => handleScriptOptionClick("save")}>Save</div>
            </div>
          )}
        </div>
        <label htmlFor="languages" className="label">Select Language:</label>
        <Select
          id="languages"
          className="languages"
          options={languageOptions}
          value={selectedLanguage}
          onChange={handleLanguageChange}
        />
        <div className="right-panel">
          <button
            className={`btn stop-btn ${isLoading ? "" : "loading"}`}
            onClick={stopExecutionHandler}
            disabled={!isLoading}
          >
            Stop
          </button>
          <button
            className={`btn run-btn ${isLoading || !wsReady ? "loading" : ""}`}
            onClick={executeCode}
            disabled={isLoading || !wsReady}
          >
            {isLoading ? <FaSpinner className="spinner" /> : !wsReady ? "..." : "Run"}
          </button>
        </div>
      </div>
      <div>
        <AceEditor
          mode="c_cpp"
          theme="monokai"
          onChange={onChangeHandler}
          name="editor"
          value={codeText}
          editorProps={{ $blockScrolling: true }}
          showPrintMargin={false}
          style={{
            width: "100%",
            height: outputActive ? "calc(70vh - 200px)" : "calc(70vh - 85px)",
          }}
        />
        {outputActive && (
          <div className="output">
            Terminal is Opened at {startTime}.<br /><br />{output}
          </div>
        )}
        {lastRunVisible && (
          <div className="output last-run-panel">
            <div className="last-run-header"><strong>Last Runs</strong></div>
            {lastDbRuns.length === 0 && <em>No runs recorded yet.</em>}
            {lastDbRuns.map((run, idx) => (
              <div key={run.id} className="last-run-entry">
                <div className="last-run-entry-meta">
                  <span className="last-run-index">#{idx + 1}</span>
                  <span>{run.language}</span>
                  <span>{formatRunTime(run.started_at)}</span>
                  <span className={`run-status run-status--${run.status}`}>{run.status}</span>
                </div>
                {run.code_snippet && (
                  <div className="last-run-script">
                    <div className="last-run-section-label">Script</div>
                    <pre>{run.code_snippet}</pre>
                  </div>
                )}
                {idx === 0 && (
                  <>
                    <div className="last-run-section-label">Output</div>
                    <div className="last-run-output">{lastRun?.output || <em>No output recorded.</em>}</div>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
        <div className="bottom-taskbar">
          <div className="terminal-icon" onClick={toggleOutput} title="Toggle terminal">
            <FaTerminal />
          </div>
          <div
            className={`terminal-icon ${lastRunVisible ? 'active' : ''}`}
            onClick={() => setLastRunVisible((v) => !v)}
            title="Last run"
          >
            <FaHistory />
          </div>
        </div>
      </div>

      <Modal
        opened={isSavePopupVisible}
        onClose={() => handleSavePopupBack()}
        title="Save Script"
        className="popup-content"
      >
        <TextInput
          onChange={(e) => setScriptNameInput(e.target.value)}
          label="Save as"
          placeholder="Enter Script Name"
        />
        <div className="popup-buttons">
          <button className="popup-btn" onClick={handleSavePopupSave}>Save</button>
        </div>
      </Modal>

      <Modal
        opened={scriptsPopupVisible}
        onClose={() => setScriptsPopupVisible(false)}
        title="Load Script"
        className="popup-content"
      >
        <Divider />
        <Space h="md" />
        {scriptsData?.getUserScripts.length > 0 ? (
          <div style={{ maxHeight: 300, overflowY: 'auto', width: '100%' }}>
            {scriptsData.getUserScripts.map((script) => (
              <Group
                key={script.script_id}
                position="apart"
                p="sm"
                className={`script-row ${selectedScripts.some((s) => s.script_id === script.script_id) ? 'highlighted' : ''}`}
              >
                <Checkbox
                  checked={selectedScripts.some((s) => s.script_id === script.script_id)}
                  label={script.script_name}
                  color="#496bd4"
                  onChange={() => toggleSelectScript(script)}
                />
                <Group>
                  <button className="delete-btn" onClick={() => handleDeleteScriptClick(script)}>Delete</button>
                  <button className="load-btn" onClick={() => handleLoadScriptClick(script)}>Load</button>
                </Group>
              </Group>
            ))}
          </div>
        ) : (
          <Text align='center'>No Script Found</Text>
        )}
        <Space h="md" />
        <Divider />
        <div style={{ textAlign: 'center' }} className="popup-buttons">
          <button onClick={toggleAllScripts} className="popup-btn">
            {selectedScripts.length === scriptsData?.getUserScripts.length ? "Deselect All" : "Select All"}
          </button>
          <button
            onClick={handleDeleteSelectedScripts}
            className="popup-btn"
            disabled={selectedScripts.length === 0}
          >
            Delete Selected
          </button>
        </div>
      </Modal>

      <Modal
        opened={isImportPopupVisible}
        onClose={() => setIsImportPopupVisible(false)}
        title="Import Script File"
        className="popup-content"
      >
        <FileInput
          onChange={handleImportFile}
          accept=".py"
          multiple={false}
          variant="filled"
          placeholder="Select a Python file"
          label="Select a Python file to import"
        />
      </Modal>
    </div>
  );
}
